import Typography from "@mui/material/Typography";
import { TFunction } from "i18next";
import isEmpty from "lodash/isEmpty";
import { TConfirmModalOptions } from "context/ConfirmationDialogContext/ConfirmContext.types";
import {
  IStructuredDocument,
  IStructuredDocumentSection,
  TPossibleECTDFileType,
} from "models/assets.models";
import { IDocument, IDocumentBase } from "models/documents.models";
import { IProjectPermissions } from "models/permissions.models";
import { TCurrentUser } from "models/users.models";
import { TTreeStructureOption } from "screens/Project/components/TreeStructureOption/TreeStructureOption.types";
import { DocumentStatus } from "utils/constants/doc.constants";
import { PDF_FILE_TYPE } from "utils/constants/doc.constants";
import { ARN_VERSION_SEPARATOR } from "utils/constants/id.constants";
import { Path } from "utils/constants/paths.constants";
import { isStructuredDocument } from "utils/helpers/documents/document";
import { TPossibleAsset } from "utils/types";
import { StructuredDocumentAction } from "./StructuredDocument.constants";

export type TStructuredDocumentSectionContent = {
  name: string;
  id?: string;
  path?: string;
  documents?: IDocumentBase[];
  sections?: TStructuredDocumentSectionContent[];
};

type TStructuredDocumentSectionWithOptionalPath = IStructuredDocumentSection & {
  path?: string;
  sections: TStructuredDocumentSectionWithOptionalPath[];
};

export const getTableOfContentsTreeView = (
  sections: IStructuredDocumentSection[] | undefined,
): TTreeStructureOption[] => {
  return getTreeStructure(sections, 1, []);
};

const getTreeStructure = (
  sections: IStructuredDocumentSection[] | undefined,
  level: number,
  parentPath: string[],
  offsetIndex = 0,
): TTreeStructureOption[] => {
  if (!sections) {
    return [];
  }

  return sections.map((section, index) => {
    const { id, name, sections: nestedSections, documentsAssets } = section;
    const childrenOffsetIndex = documentsAssets?.length ?? 0;

    const path = [...parentPath, (index + offsetIndex).toString()];

    return {
      id,
      label: name,
      nestedLevel: level,
      path,
      expanded: false,
      children: [
        ...(documentsAssets ?? []).map((asset, assetIndex) => ({
          id: asset.id,
          label: asset.name,
          nestedLevel: level + 1,
          path: [...path, "documentsAssets", assetIndex.toString()],
          expanded: false,
          fileType: PDF_FILE_TYPE as TPossibleECTDFileType,
          children: [],
        })),
        ...getTreeStructure(
          nestedSections ?? [],
          level + 1,
          [...path, "children"],
          childrenOffsetIndex,
        ),
      ],
    };
  });
};

export const getStructuredDocumentOptionById = (
  StructuredDocumentOptions: TTreeStructureOption[],
  documentId: string,
): TTreeStructureOption | null =>
  StructuredDocumentOptions.reduce<TTreeStructureOption | null>(
    (found, option) =>
      found ||
      (option.id === documentId
        ? option
        : option.children.length > 0
          ? getStructuredDocumentOptionById(option.children, documentId)
          : null),
    null,
  );

export const getContentVersion = (document?: IStructuredDocument) =>
  document?.content?.versions?.[0]?.version;

export const getSourceARNWithVersion = (
  document: IDocument,
  version: string | undefined,
) => {
  if (!version) {
    return document.source.arn;
  }

  return `${document.source.arn}${ARN_VERSION_SEPARATOR}${version}`;
};

export const isLatestVersion = (document: TPossibleAsset) => {
  if (isStructuredDocument(document)) {
    return getContentVersion(document) === document?.latestVersion?.version;
  } else {
    // ToDo: For all other asset types we'll implement their own ways to define if version is the latest
    return true;
  }
};

export function getARNWithoutVersion(
  ARN: string,
  versionSeparator: string = ARN_VERSION_SEPARATOR,
): string {
  const versionPattern = new RegExp(`${versionSeparator}\\d+\\.\\d+$`);

  return ARN.replace(versionPattern, "");
}

export const isVersionChanged = (prevARN: string, nextARN: string): boolean => {
  const isARNWithoutVersionTheSame =
    getARNWithoutVersion(prevARN) === getARNWithoutVersion(nextARN);

  const isARNChanged = prevARN !== nextARN;

  return isARNWithoutVersionTheSame && isARNChanged;
};

export const getPreparedSections = (
  sections?: TStructuredDocumentSectionWithOptionalPath[],
): TStructuredDocumentSectionContent[] => {
  return (
    sections?.map(
      ({
        documentsAssets: _documentsAssets,
        path: _path,
        sections: nestedSections,
        ...section
      }) => ({
        ...section,
        sections: getPreparedSections(nestedSections),
      }),
    ) ?? []
  );
};

type TGetStructuredDocumentActionsParams = {
  document?: IStructuredDocument;
  user: TCurrentUser | undefined;
  permissions: IProjectPermissions | undefined;
  isProjectActive: boolean;
  isEditStructuredDocumentSectionTitleEnabled: boolean;
};

export const getStructuredDocumentActions = ({
  document,
  user,
  permissions,
  isProjectActive,
  isEditStructuredDocumentSectionTitleEnabled,
}: TGetStructuredDocumentActionsParams) => {
  const actions = new Set<StructuredDocumentAction>([
    StructuredDocumentAction.View,
    StructuredDocumentAction.Download,
  ]);

  const isSameTenant =
    document?.source?.tenantType === user?.organization?.type;

  const isDocumentExist = !!document;

  const canChangeContent = permissions?.canEditContent;

  if (
    isDocumentExist &&
    isProjectActive &&
    isSameTenant &&
    canChangeContent &&
    isLatestVersion(document)
  ) {
    if (
      document.status === DocumentStatus.InVerification ||
      document.status === DocumentStatus.Final
    ) {
      actions.add(StructuredDocumentAction.RemoveFile);

      if (!!permissions?.canManageStructuredDocument) {
        actions.add(StructuredDocumentAction.AddSection);
        actions.add(StructuredDocumentAction.DeleteSection);

        if (isEditStructuredDocumentSectionTitleEnabled) {
          actions.add(StructuredDocumentAction.EditSectionTitle);
        }

        actions.add(StructuredDocumentAction.AddSubsection);
      }
    }

    if (document.status === DocumentStatus.InVerification) {
      actions.add(StructuredDocumentAction.AttachDocumentToSection);
    }

    if (document.status === DocumentStatus.Final) {
      actions.add(StructuredDocumentAction.AddDocument);
    }
  }

  return actions;
};

export const hasDocumentsInAnySection = (
  sections: IStructuredDocumentSection[] | undefined,
): boolean =>
  sections?.some(
    ({ documents, documentsAssets, sections: nestedSections }) =>
      !isEmpty(documents) ||
      !isEmpty(documentsAssets) ||
      hasDocumentsInAnySection(nestedSections),
  ) ?? false;

export const isStructuredDocumentNonEmpty = (
  document: IStructuredDocument | undefined,
) => {
  if (hasDocumentsInAnySection(document?.content?.contentSections)) {
    return true;
  }

  if (!document?.content?.content) {
    return false;
  }

  try {
    const parsedContent = JSON.parse(document.content.content) as
      | { sections?: IStructuredDocumentSection[] }
      | IStructuredDocumentSection[];

    const parsedSections = Array.isArray(parsedContent)
      ? parsedContent
      : parsedContent.sections;

    return hasDocumentsInAnySection(parsedSections);
  } catch {
    return false;
  }
};

export const getUpdateConfirmationModalContent = (
  t: TFunction,
  title?: string,
): TConfirmModalOptions => ({
  title: title ?? t("structuredDocument.addSubsectionModal.editSectionTitle"),
  content: (
    <Typography variant="subtitle2">
      {t("structuredDocument.editSectionModal.alertMessage")}
    </Typography>
  ),
  confirmButtonText: t("button.proceed", { ns: "common" }),
  confirmColor: "primary",
});

export const getConfirmModalContentForDocumentInFinal_deprecated = (
  t: TFunction,
  title?: string,
): TConfirmModalOptions => ({
  title: title ?? t("structuredDocument.addSubsectionModal.editSectionTitle"),
  content: (
    <Typography variant="subtitle2">
      {t(
        "structuredDocument.editSectionModal.noteForDocumentInFinal_deprecated",
      )}
    </Typography>
  ),
  confirmButtonText: t("button.proceed", { ns: "common" }),
  confirmColor: "primary",
});

export const getDeleteConfirmationModalContent = (
  t: TFunction,
): TConfirmModalOptions => ({
  title: t("structuredDocument.deleteSectionModal.title"),
  content: (
    <>
      <Typography mb={2} variant="subtitle2">
        {t("structuredDocument.deleteSectionModal.noteForDeleteSection")}
      </Typography>

      <Typography variant="subtitle2">
        {t("structuredDocument.deleteSectionModal.noteForPermanentRemoval")}
      </Typography>
    </>
  ),
  confirmButtonText: t("structuredDocument.sections.actions.deleteSection"),
  confirmColor: "error",
});

export const getStructuredDocumentSectionsPath = (
  projectId: string,
  structuredDocumentId: string,
  version?: string,
) => {
  /*TODO(KM): This will be fixed in FTE-56873
   *once the backend is updated to accept the version parameter and return the correct document by the unversioned ARN
   */
  const assetId = getARNWithoutVersion(structuredDocumentId);

  return (
    Path.ProjectStructuredDocumentSections.replace(
      ":projectId",
      projectId,
    ).replace(":structuredDocumentId", assetId) +
    (version ? `?version=${version}` : "")
  );
};
